// packages/front/src/portfolio/modals/add-transaction-modal.tsx
//
// Stage 3 (part 3) — Multi-row "Add Transaction" modal.
//
// Each row = one deal (buy/sell + symbol + qty + price + date).
// "Add row" appends an empty row; "Save all" persists every row via
// useCreateTransaction (Promise.allSettled). Successful rows are removed;
// failed rows stay with an `error` message. The modal stays open until all
// rows succeed (or the user cancels).
//
// Edit mode: rows = [single row seeded from initialData]. Add/Remove buttons
// are hidden. Save → updateTx(rows[0]).
//
// Account is shared across all rows (selected once at the top).

import { useEffect, useMemo, useState, type FC, type FormEvent } from "react";
import {
  Button,
  formatDisplayNumber,
  Modal,
  Select,
  SymbolCombobox,
  toDateInput,
} from "../../_common";
import { CURRENCY_LIST, PROMISE_SETTLED_STATUS_OBJ, TRANSACTION_TYPE_OBJ } from "@signals/shared";
import {
  useCreateTransaction,
  useUpdateTransaction,
  usePortfolioAccounts,
  type Transaction,
} from "../portfolio.hooks";
import { useSymbols, type SymbolItem } from "../../symbol";
import { apiErrorMessage } from "../portfolio-api-error";
import { accountUnavailableMessage, operationAccountOptions } from "../portfolio-account-availability";

export type { Transaction } from "../portfolio.hooks";

export interface AddTransactionModalProps {
  open: boolean;
  onClose: () => void;
  portfolioId: string;
  editId?: string | undefined;
  initialData?: Partial<Transaction> | undefined;
}

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const numberClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm [appearance:textfield]";
const labelClass = "text-sm font-medium";

// ── Row model ────────────────────────────────────────────────────────────────

interface TransactionRow {
  symbolId: string;
  symbolLabel: string | null;
  symbolCurrency: string | null;
  type: "buy" | "sell";
  quantity: string;
  blocked: boolean;
  blockedQuantity: string;
  openQuantity?: number;
  price: string;
  currency: string;
  commission: string;
  commissionCurrency: string;
  date: string;
  note: string;
  error?: string;
}

function emptyRow(defaultCurrency: string): TransactionRow {
  return {
    symbolId: "",
    symbolLabel: null,
    symbolCurrency: null,
    type: "buy",
    quantity: "",
    blocked: false,
    blockedQuantity: "0",
    price: "",
    currency: defaultCurrency,
    commission: "",
    commissionCurrency: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
  };
}

function rowFromInitialData(
  data: Partial<Transaction>,
  defaultCurrency: string,
): TransactionRow {
  const ticker = data.symbolTicker ?? "";
  const name = data.symbolName ?? "";
  return {
    symbolId: data.symbolId ?? "",
    symbolLabel: ticker ? (name ? `${ticker} — ${name}` : ticker) : null,
    symbolCurrency: null,
    type: data.type ?? "buy",
    quantity: data.quantity != null ? String(data.quantity) : "",
    blocked: (data.blockedQuantity ?? 0) > 0,
    blockedQuantity: data.blockedQuantity != null ? String(data.blockedQuantity) : "0",
    openQuantity: data.openQuantity ?? data.quantity ?? 0,
    price: data.price != null ? String(data.price) : "",
    currency: data.currency ?? defaultCurrency,
    commission: data.commission != null ? String(data.commission) : "",
    commissionCurrency: data.commissionCurrency ?? "",
    date: toDateInput(data.date),
    note: data.note ?? "",
  };
}

// ── Validation ───────────────────────────────────────────────────────────────

function validateRow(row: TransactionRow): string | null {
  if (!row.symbolId) return "Symbol is required";
  if (!row.type) return "Type is required";
  if (!row.quantity) return "Quantity is required";
  if (!row.price) return "Price is required";
  if (!row.currency) return "Currency is required";
  if (!row.date) return "Date is required";
  if (row.blocked) {
    const blockedQuantity = Number(row.blockedQuantity);
    if (!Number.isFinite(blockedQuantity) || blockedQuantity <= 0) {
      return "Blocked quantity must be greater than 0";
    }
    if (blockedQuantity > Number(row.quantity)) {
      return "Blocked quantity cannot exceed the purchase quantity";
    }
  }
  return null;
}

// ── Component ────────────────────────────────────────────────────────────────

export const AddTransactionModal: FC<AddTransactionModalProps> = ({
  open,
  onClose,
  portfolioId,
  editId,
  initialData,
}) => {
  const isEdit = Boolean(editId);
  const accountsQuery = usePortfolioAccounts(portfolioId);
  const accounts = accountsQuery.data?.items ?? [];
  // Fetch full symbol list (limit 500) to look up `price` on selection.
  // The combobox search API only returns {id, symbol, name, currency} — no price.
  const symbolsQuery = useSymbols({ limit: 500 });
  const symbolsById = useMemo(() => {
    const map = new Map<string, SymbolItem>();
    for (const s of symbolsQuery.data?.items ?? []) map.set(s.id, s);
    return map;
  }, [symbolsQuery.data]);
  const createTx = useCreateTransaction(portfolioId);
  const updateTx = useUpdateTransaction(portfolioId);
  const saving = createTx.isPending || updateTx.isPending;

  const [accountId, setAccountId] = useState("");
  const [rows, setRows] = useState<TransactionRow[]>([emptyRow("")]);

  const accountCurrency = useMemo(() => {
    const acc = accounts.find((a) => a.id === accountId);
    return acc?.currency ?? null;
  }, [accounts, accountId]);

  // Availability follows the operation dates: a closed account takes operations up to its closure date.
  const operationDates = useMemo(() => rows.map((row) => row.date), [rows]);
  const accountOptions = useMemo(
    () => operationAccountOptions(accounts, operationDates, [accountId, initialData?.accountId]),
    [accounts, operationDates, accountId, initialData?.accountId],
  );
  const accountError = useMemo(
    () => accountUnavailableMessage(accounts.find((a) => a.id === accountId), operationDates),
    [accounts, accountId, operationDates],
  );

  // Seed form when opening.
  useEffect(() => {
    if (!open) return;
    if (isEdit && initialData) {
      const accCur =
        accounts.find((a) => a.id === (initialData.accountId ?? ""))?.currency ?? "";
      if (initialData.accountId !== undefined) setAccountId(initialData.accountId);
      setRows([rowFromInitialData(initialData, accCur)]);
    } else {
      // Create mode: if initialData has any row fields (e.g. Sell from a
      // position), seed the first row from it; otherwise start empty.
      const accCur =
        initialData?.accountId
          ? accounts.find((a) => a.id === initialData.accountId)?.currency ?? ""
          : "";
      setAccountId(initialData?.accountId ?? "");
      const hasPrefill =
        initialData &&
        (initialData.symbolId !== undefined ||
          initialData.type !== undefined ||
          initialData.quantity !== undefined ||
          initialData.price !== undefined);
      setRows([
        hasPrefill
          ? rowFromInitialData(initialData, accCur)
          : emptyRow(accCur),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Default currency from account when a row has no currency and no symbol currency.
  useEffect(() => {
    if (!accountCurrency) return;
    setRows((prev) =>
      prev.map((r) =>
        !r.currency && !r.symbolCurrency ? { ...r, currency: accountCurrency } : r,
      ),
    );
  }, [accountCurrency]);

  // ── Row helpers ────────────────────────────────────────────────────────────

  const updateRow = (idx: number, patch: Partial<TransactionRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    const accCur = accountCurrency ?? "";
    setRows((prev) => [...prev, emptyRow(accCur)]);
  };

  const removeRow = (idx: number) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  };

  // ── Summary (right column) ─────────────────────────────────────────────────

  const summary = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    const byCurrency = new Map<string, { debit: number; credit: number }>();
    for (const r of rows) {
      const q = Number(r.quantity || 0);
      const p = Number(r.price || 0);
      const c = Number(r.commission || 0);
      if (Number.isNaN(q) || Number.isNaN(p)) continue;
        const value = r.type === TRANSACTION_TYPE_OBJ.SELL ? q * p - c : q * p + c;
      const cur = r.currency || "USD";
      const entry = byCurrency.get(cur) ?? { debit: 0, credit: 0 };
        if (r.type === TRANSACTION_TYPE_OBJ.SELL) {
        totalCredit += value;
        entry.credit += value;
      } else {
        totalDebit += value;
        entry.debit += value;
      }
      byCurrency.set(cur, entry);
    }
    return { totalDebit, totalCredit, byCurrency };
  }, [rows]);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!accountId || accountError) return;

    // Validate all rows first. Attach `error` only when validation fails
    // (exactOptionalPropertyTypes: `error?: string` cannot hold `undefined`).
    const validationErrors = rows.map((r) => validateRow(r));
    if (validationErrors.some((err) => err !== null)) {
      setRows(
        rows.map((r, i) => {
          const err = validationErrors[i];
          if (err) return { ...r, error: err };
          const { error: _drop, ...rest } = r;
          return rest;
        }),
      );
      return;
    }

    if (isEdit && editId) {
      // Edit mode: single row → updateTx.
      const r = rows[0];
      if (!r) return;
      const payload = {
        accountId,
        symbolId: r.symbolId,
        type: r.type,
        currency: r.currency || "USD",
        date: r.date,
        commission: Number(r.commission) || 0,
        ...(r.commissionCurrency
          ? { commissionCurrency: r.commissionCurrency }
          : {}),
        note: r.note,
        quantity: Number(r.quantity) || 0,
        price: Number(r.price) || 0,
        blockedQuantity: r.type === TRANSACTION_TYPE_OBJ.BUY && r.blocked ? Number(r.blockedQuantity) || 0 : 0,
      };
      try {
        await updateTx.mutateAsync({ id: editId, body: payload });
        onClose();
      } catch (error) {
        setRows([{ ...r, error: apiErrorMessage(error, "Failed to update transaction") }]);
      }
      return;
    }

    // Create mode: save all rows in parallel.
    const results = await Promise.allSettled(
      rows.map((r) => {
        const payload = {
          accountId,
          symbolId: r.symbolId,
          type: r.type,
          currency: r.currency || "USD",
          date: r.date,
          commission: Number(r.commission) || 0,
          ...(r.commissionCurrency
            ? { commissionCurrency: r.commissionCurrency }
            : {}),
          note: r.note,
          quantity: Number(r.quantity) || 0,
          price: Number(r.price) || 0,
          blockedQuantity: r.type === TRANSACTION_TYPE_OBJ.BUY && r.blocked ? Number(r.blockedQuantity) || 0 : 0,
        };
        return createTx.mutateAsync(payload);
      }),
    );

    const remaining: TransactionRow[] = [];
    rows.forEach((r, i) => {
      const res = results[i];
      if (!res) return;
      if (res.status === PROMISE_SETTLED_STATUS_OBJ.FULFILLED) return; // success → drop row
      const msg =
        apiErrorMessage(res.reason, "Failed to create transaction");
      remaining.push({ ...r, error: msg });
    });

    if (remaining.length === 0) {
      onClose();
    } else {
      setRows(remaining);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit Transaction" : "Add Transaction"}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Account spans full width (applies to all rows) */}
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Account</label>
          <Select
            value={accountId}
            onChange={setAccountId}
            options={accountOptions}
            placeholder={accounts.length === 0 ? "No accounts yet — create one first" : "Select account"}
            ariaLabel="Account"
            disabled={accounts.length === 0}
          />
          {accountError && (
            <p role="alert" className="text-sm text-destructive">
              {accountError}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* ── Left column: rows ────────────────────────────────────────── */}
          <div className="flex flex-col gap-4">
            {rows.map((row, idx) => {
              const sym = row.symbolId ? symbolsById.get(row.symbolId) : undefined;
              const symbolPrice = sym?.price ?? null;
              const computedAmount = (() => {
                const q = Number(row.quantity || 0);
                const p = Number(row.price || 0);
                const c = Number(row.commission || 0);
      const v = row.type === TRANSACTION_TYPE_OBJ.SELL ? q * p - c : q * p + c;
                return Number.isNaN(v)
                  ? "—"
                  : formatDisplayNumber(v, { maximumFractionDigits: 2 });
              })();
              return (
                <div
                  key={idx}
                  className="flex flex-col gap-3 rounded-md border border-border p-3"
                >
                  {/* Row header: index + remove button */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Row {idx + 1}
                    </span>
                    {!isEdit && rows.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-6 px-2 text-xs"
                        onClick={() => removeRow(idx)}
                      >
                        × Remove
                      </Button>
                    )}
                  </div>

                  {row.error && (
                    <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
                      {row.error}
                    </div>
                  )}

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Symbol</label>
                    <SymbolCombobox
                      value={row.symbolId}
                      onChange={(symbolId) => updateRow(idx, { symbolId })}
                      onSelectItem={(item) => {
                        updateRow(idx, {
                          symbolCurrency: item.currency ?? null,
                          currency: item.currency ?? row.currency,
                        });
                      }}
                      initialLabel={row.symbolLabel ?? undefined}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Type</label>
                    <Select
                      value={row.type}
                      onChange={(value) => {
                        const selectedType = ["buy", "sell"].find((type) => type === value);
    if (selectedType === TRANSACTION_TYPE_OBJ.BUY || selectedType === TRANSACTION_TYPE_OBJ.SELL) {
                          updateRow(idx, {
                            type: selectedType,
                            ...(selectedType === TRANSACTION_TYPE_OBJ.SELL
                              ? { blocked: false, blockedQuantity: "0" }
                              : {}),
                          });
                        }
                      }}
                      options={[{ value: "buy", label: "Buy" }, { value: "sell", label: "Sell" }]}
                      ariaLabel={`Transaction type for row ${idx + 1}`}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Quantity</label>
                    <input
                      className={numberClass}
                      type="number"
                      step="any"
                      value={row.quantity}
                      onChange={(e) => {
                        const quantity = e.target.value;
                        const currentBlocked = Number(row.blockedQuantity) || 0;
                        updateRow(idx, {
                          quantity,
                          ...(currentBlocked > Number(quantity || 0)
                            ? { blockedQuantity: quantity || "0" }
                            : {}),
                        });
                      }}
                      required
                    />
                  </div>
                  {row.type === TRANSACTION_TYPE_OBJ.BUY && (
                    <div className="flex flex-col gap-2 rounded-md border border-amber-300/70 bg-amber-50/40 p-3 dark:bg-amber-950/10">
                      <label className="flex items-center gap-2 text-sm font-medium">
                        <input
                          type="checkbox"
                          checked={row.blocked}
                          disabled={!(Number(row.quantity) > 0) || (row.openQuantity !== undefined && row.openQuantity <= 0)}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              updateRow(idx, { blocked: false, blockedQuantity: "0" });
                              return;
                            }
                            const available = row.openQuantity ?? (Number(row.quantity) || 0);
                            updateRow(idx, {
                              blocked: true,
                              blockedQuantity: String(Math.min(available, Number(row.quantity) || 0)),
                            });
                          }}
                        />
                        This purchase is blocked
                      </label>
                      {row.blocked && (
                        <div className="flex flex-col gap-1">
                          <label className={labelClass}>Blocked quantity</label>
                          <input
                            className={numberClass}
                            type="number"
                            min="0"
                            max={row.quantity || undefined}
                            step="any"
                            value={row.blockedQuantity}
                            onChange={(e) => updateRow(idx, { blockedQuantity: e.target.value })}
                          />
                          {isEdit && row.openQuantity !== undefined && (
                            <p className="text-xs text-muted-foreground">
                              Remaining from this purchase: {formatDisplayNumber(row.openQuantity, { maximumFractionDigits: 8 })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Price</label>
                    <input
                      className={numberClass}
                      type="number"
                      step="any"
                      value={row.price}
                      onChange={(e) => updateRow(idx, { price: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Commission</label>
                    <input
                      className={numberClass}
                      type="number"
                      step="any"
                      value={row.commission}
                      onChange={(e) => updateRow(idx, { commission: e.target.value })}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Commission Currency</label>
                    <Select
                      value={row.commissionCurrency}
                      onChange={(commissionCurrency) => updateRow(idx, { commissionCurrency })}
                      options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
                      placeholder="Select currency"
                      ariaLabel={`Commission currency for row ${idx + 1}`}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Date</label>
                    <input
                      className={inputClass}
                      type="date"
                      value={row.date}
                      onChange={(e) => updateRow(idx, { date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label htmlFor={`transaction-note-${idx}`} className={labelClass}>Note</label>
                    <input
                      id={`transaction-note-${idx}`}
                      className={inputClass}
                      value={row.note}
                      onChange={(e) => updateRow(idx, { note: e.target.value })}
                    />
                  </div>

                  {/* Per-row computed amount (compact) */}
                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Amount (auto)</label>
                    <div className={inputClass}>
                      {computedAmount ?? "—"}
                      {row.currency ? ` ${row.currency}` : ""}
                    </div>
                    <p className="text-xs text-muted-foreground">
                {row.type === TRANSACTION_TYPE_OBJ.SELL
                        ? "qty × price − commission"
                        : "qty × price + commission"}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Deal currency</label>
                    <Select
                      value={row.currency}
                      onChange={(currency) => updateRow(idx, { currency })}
                      options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
                      placeholder="Select currency"
                      ariaLabel={`Deal currency for row ${idx + 1}`}
                    />
                  </div>

                  {/* Reference fields */}
                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Symbol currency</label>
                    <div className={inputClass}>{row.symbolCurrency ?? "—"}</div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className={labelClass}>Current FMP price</label>
                    <div className={inputClass}>
                      {symbolPrice != null
                        ? formatDisplayNumber(symbolPrice, { maximumFractionDigits: 4 })
                        : "—"}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Add row button (create mode only) */}
            {!isEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={addRow}
              >
                + Add row
              </Button>
            )}
          </div>

          {/* ── Right column: summary ────────────────────────────────────── */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelClass}>Account currency</label>
              <div className={inputClass}>{accountCurrency ?? "—"}</div>
            </div>

            <div className="flex flex-col gap-1">
              <label className={labelClass}>Total debit (buys)</label>
              <div className={inputClass}>
                {formatDisplayNumber(summary.totalDebit, { maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Sum of (qty × price + commission) across buy rows
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className={labelClass}>Total credit (sells)</label>
              <div className={inputClass}>
                {formatDisplayNumber(summary.totalCredit, { maximumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Sum of (qty × price − commission) across sell rows
              </p>
            </div>

            {/* Breakdown by currency */}
            <div className="flex flex-col gap-1">
              <label className={labelClass}>By currency</label>
              <div className="flex flex-col gap-1">
                {summary.byCurrency.size === 0 && (
                  <div className={inputClass}>—</div>
                )}
                {Array.from(summary.byCurrency.entries()).map(([cur, vals]) => (
                  <div
                    key={cur}
                    className="flex items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm"
                  >
                    <span className="font-medium">{cur}</span>
                    <span className="text-muted-foreground">
                      debit {formatDisplayNumber(vals.debit, { maximumFractionDigits: 2 })}
                      {" / "}
                      credit {formatDisplayNumber(vals.credit, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className={labelClass}>Rows</label>
              <div className={inputClass}>{rows.length}</div>
            </div>
          </div>
        </div>

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || accountError !== null}>
            {saving ? "Saving..." : isEdit ? "Save" : "Save all"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
